// "Create your company" (NoCompanyView.createCard, fresh-company case): company name and the admin's first and last
// name. The console offers no sample data (it is an iPhone feature) and has no earlier local company to upload, so
// creating never asks first, like iOS when there is nothing to upload or remove.

import { Building, CirclePlus, ShieldCheck, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Button, Field, GT_COLORS, IconBadge, Input, SectionCard } from '@/ui';
import { cleanCompanyName, COMPANY_NAME_MAX, type ProfileName } from '../companySetup';
import type { CreateInput } from '../useCompanySetup';
import { createDescription, INVITE_BULLET, ROLES_BULLET } from '../welcomeText';
import { Bullet, ErrorNote } from './Bits';

export interface CreateCompanySectionProps {
  /** Prefill for the admin's name (from the account), read once. */
  initialName: ProfileName;
  creating: boolean;
  error: string | null;
  /** Something else is running: the fields and the button are off. */
  disabled: boolean;
  onCreate: (input: CreateInput) => void;
}

export function CreateCompanySection({ initialName, creating, error, disabled, onCreate }: CreateCompanySectionProps) {
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState(initialName.firstName);
  const [lastName, setLastName] = useState(initialName.lastName);

  const cleanName = cleanCompanyName(companyName);
  const first = firstName.trim();
  const canCreate = cleanName !== '' && first !== '';
  const locked = disabled || creating;

  return (
    <SectionCard title="Create your company" bodyClassName="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <IconBadge icon={<Building />} color={GT_COLORS.teal} size={44} />
        {/* Not a live region: it follows the name field, and would be read out on every keystroke. */}
        <p className="pt-0.5 text-sm text-fg">
          {createDescription(cleanName)}
        </p>
      </div>
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canCreate && !locked) {
            onCreate({ companyName: cleanName, firstName: first, lastName: lastName.replace(/\s+/g, ' ').trim() });
          }
        }}
      >
        <Field label="Company name" required>
          <Input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            autoComplete="organization"
            maxLength={COMPANY_NAME_MAX}
            disabled={locked}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" required>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              disabled={locked}
            />
          </Field>
          <Field label="Last name">
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              disabled={locked}
            />
          </Field>
        </div>
        <div className="flex flex-col gap-3">
          <Bullet icon={<UserPlus />} color={GT_COLORS.orange}>
            {INVITE_BULLET}
          </Bullet>
          <Bullet icon={<ShieldCheck />} color={GT_COLORS.purple}>
            {ROLES_BULLET}
          </Bullet>
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          icon={<CirclePlus />}
          loading={creating}
          disabled={!canCreate || disabled}
        >
          {creating ? 'Creating…' : 'Create company'}
        </Button>
      </form>
      {error && <ErrorNote>{error}</ErrorNote>}
    </SectionCard>
  );
}
